import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import QuickSearchResult from './QuickSearchResult';
import { translate } from '../Services/translation';
import {
  query,
  setSearchQuery,
  toggleShowFullForm,
  handleKeyDown,
  handleClickSearch,
} from '../Actions/QuickSearch.action';
import usePrevious from '@react-hook/previous';
import { debounce } from 'lodash-es';
import SmdIcon from './SmdIcon';

// debouncing function to 200ms so we don't send query request on every key stroke
const debouncedQuery = debounce(
  (dispatch, text) => dispatch(query(text, false)),
  200
);

// Inline search icon shared by the redesign trigger button and the expanded
// form, so the popup field's icon matches the header icon exactly (SMD-50).
const SearchIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <path d="m21 21-4.34-4.34" />
    <circle cx="11" cy="11" r="8" />
  </svg>
);

const QuickSearch = ({ mode = 'legacy' }) => {
  const { query, result, showResult, showFullForm, selectedItem } = useSelector(
    (state) => state.quickSearch
  );
  const [lastClickedNode, setLastClickedNode] = useState(null);
  const searchUrl =
    window.__litium.quickSearchUrl + (query.length > 0 ? `?q=${query}` : '');
  const searchContainer = useRef(null);
  const searchInput = useRef(null);
  const dispatch = useDispatch();
  const previousSelectedItem = usePrevious(selectedItem);

  const clickHandler = useCallback((event) => {
    setLastClickedNode(event.target);
  }, []);
  useEffect(() => {
    // listen for click event to hide the search when clicking outside
    window.addEventListener('mousedown', clickHandler);
    return () => window.removeEventListener('mousedown', clickHandler);
  }, [clickHandler]);

  useEffect(() => {
    // set query value if it is avaialble in the Url
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('q')) {
      const query = urlParams.get('q');
      dispatch(setSearchQuery(query));
    }
  }, [dispatch]);

  useEffect(() => {
    if (selectedItem !== previousSelectedItem) {
      const el = document.querySelector('.quick-search-result__item--selected');
      el &&
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest',
        });
    }
  }, [selectedItem, previousSelectedItem]);

  // The hover wash lives on the trigger <a>, not the container — the container also
  // wraps the result popup, so a container hover would bleed white text into the results.
  const isRedesign = mode === 'redesign-inline';
  const containerClass = isRedesign
    ? 'quick-search quick-search--redesign-inline relative inline-flex bg-transparent'
    : 'quick-search';
  const triggerClass = isRedesign
    ? 'quick-search__link--block inline-flex items-center justify-center p-0 w-10 md:w-12 h-10 md:h-12 rounded-full bg-transparent hover:bg-brand-dark-green hover:text-off-white transition-colors text-brand-dark-green'
    : 'quick-search__link--block';

  return (
    <div className={containerClass} role="search" ref={searchContainer}>
      <a
        className={triggerClass}
        rel="nofollow"
        href="#"
        title={translate('general.search')}
        onClick={(e) => {
          e.preventDefault();
          dispatch(toggleShowFullForm(false));
          setTimeout(() => {
            searchInput.current && searchInput.current.focus();
          }, 0);
        }}
      >
        {isRedesign ? (
          <SearchIcon className="w-6 h-6" />
        ) : (
          <i className="quick-search__icon"></i>
        )}
        <span className="quick-search__title hide">
          {translate('general.search')}
        </span>
      </a>
      <div
        className={`quick-search__form ${
          showFullForm ? 'quick-search__form--force-show' : ''
        }`}
        role="search"
      >
        {isRedesign ? (
          <span
            className="quick-search__icon"
            aria-hidden="true"
            onClick={() => dispatch(toggleShowFullForm(false))}
          >
            <SearchIcon className="w-5 h-5" />
          </span>
        ) : (
          <SmdIcon className="quick-search__icon" icon="search" onClick={(e) => dispatch(toggleShowFullForm(false))} />
        )}
        <input
          className="quick-search__input"
          type="search"
          placeholder={translate('general.search')}
          autoComplete="off"
          value={decodeURIComponent(query)}
          onChange={(event) => {
            const text = encodeURIComponent(event.target.value);
            dispatch(setSearchQuery(text));
            debouncedQuery(dispatch, text);
          }}
          onKeyDown={(event) =>
            dispatch(handleKeyDown(event, { searchUrl }, false))
          }
          ref={searchInput}
          onBlur={() => {
            if (
              searchContainer.current &&
              !searchContainer.current.contains(lastClickedNode)
            ) {
              showFullForm && dispatch(toggleShowFullForm(false));
            }
          }}
        />
        {showResult && (
          <QuickSearchResult
            result={result}
            selectedItem={selectedItem}
            searchUrl={searchUrl}
            isBigSearch={false}
          />
        )}
      </div>
    </div>
  );
};

export default QuickSearch;
